<template>
  <div class="inline-flex flex-col w-full">
    <slot name="label">
      <label
        v-if="hasLabel"
        :for="`${label}_input`"
        class="mb-1 tracking-wider text-gray-800"
      >
        {{ label }}
      </label>
    </slot>

    <div
      style="height: 37px;"
      class="relative transition-all duration-300 ease-out border rounded cursor-pointer group"
      :class="{
        'border-gray-500': !isDraggingOver,
        'shadow-outline': hasFocus && !isDraggingOver,
        'border-orange-mia shadow-outline-mia': isDraggingOver,
        'shadow-outline-error border-red-500': !hasFocus && !isDraggingOver && hasErrors
      }"
    >
      <transition
        enter-class="opacity-0"
        enter-active-class="transition transition-opacity duration-300 ease-out"
        enter-to-class="opacity-100"
        leave-class="opacity-100"
        leave-active-class="transition transition-opacity duration-300 ease-out"
        leave-to-class="opacity-0"
      >
        <div
          v-if="isDraggingOver"
          key="dragOverText"
          class="absolute inset-0 z-10"
        >
          <div
            class="absolute inset-0 opacity-25 bg-orange-mia"
            style="z-index: 1;"
          />
          <div
            class="absolute inset-0 flex items-center justify-center text-sm"
            style="z-index: 2;"
          >
            Datei hier ablegen
          </div>
        </div>
        <div
          v-else
          key="default"
          class="absolute inset-0 z-10 flex p-1 pr-2"
        >
          <button
            type="button"
            tabindex="-1"
            class="flex-shrink-0 w-1/3 text-sm text-white transition-colors duration-100 ease-out bg-blue-400 rounded shadow group-hover:bg-blue-500"
          >
            Datei auswählen
          </button>
          <div class="flex items-center flex-1 px-2 overflow-hidden whitespace-no-wrap">
            <template v-if="allowsMultiple && hasFile">
              {{ file.length }} Dateien ausgewählt
            </template>
            <template v-else>
              {{ file ? file.name : fileLabel }}
            </template>
          </div>
        </div>
      </transition>
      <div class="absolute inset-0 z-20">
        <input
          ref="fileInput"
          type="file"
          v-bind="$attrs"
          class="w-full h-full opacity-0 cursor-pointer"
          :accept="accept"
          v-on="listeners"
        >
      </div>
    </div>

    <mia-fade-transition>
      <div
        v-if="hasErrors"
        class="relative"
      >
        <small
          class="absolute top-0 block mt-1.5 leading-3 text-red-600"
          v-text="errors[0]"
        />
      </div>
    </mia-fade-transition>
  </div>
</template>

<script>
export default {
  name: 'MiaFileInput',

  props: {
    value: {
      type: [Object, File],
      default: null,
      validator: () => true
    },

    label: {
      type: String,
      default: ''
    },

    fileLabel: {
      type: String,
      default: 'Keine Datei ausgewählt'
    },

    errors: {
      type: Array,
      default() {
        return []
      }
    },

    accept: {
      type: String,
      default: undefined
    }
  },

  data: () => ({
    hasFocus: false,
    isDraggingOver: false,
    file: null
  }),

  computed: {
    listeners() {
      return {
        ...this.$listeners,
        input: () => {},
        focus: (event) => {
          this.onFocus(event)
        },
        blur: (event) => {
          this.onBlur(event)
        },
        dragover: (event) => {
          this.onDragOver(event)
        },
        change: (event) => {
          this.onFileChange(event)
        }
      }
    },

    hasLabel() {
      return this.label.length > 0
    },

    hasFile() {
      return this.file !== null
    },

    allowsMultiple() {
      return this.$attrs.multiple !== undefined
    },

    hasErrors() {
      return this.errors.length > 0
    }
  },

  watch: {
    value(value) {
      if (value === null) {
        this.$refs.fileInput.value = ''
        this.file = null
        this.$emit('input', null)
      }
    }
  },

  beforeDestroy() {
    if (this.dragOutTimer) {
      window.clearTimeout(this.dragOutTimer)
      this.dragOutTimer = undefined
    }
  },

  methods: {
    onFocus(event) {
      this.hasFocus = true
      this.$emit('focus', event)
    },

    onBlur(event) {
      this.hasFocus = false
      this.$emit('blur', event)
    },

    onDragOver(event) {
      this.isDraggingOver = true

      if (this.dragOutTimer) {
        window.clearTimeout(this.dragOutTimer)
      }

      this.dragOutTimer = window.setTimeout(() => {
        this.dragOutTimer = undefined
        this.isDraggingOver = false
      }, 100)

      this.$emit('dragover', event)
    },

    onFileChange(event) {
      if (event.target.files.length === 0) {
        this.file = null
      } else {
        this.file = this.allowsMultiple ? Object.values(event.target.files) : event.target.files[0]
      }

      if (!this.accept) {
        this.$emit('input', this.file)
        return
      }

      const passesAccept = (Array.isArray(this.file) ? this.file : [this.file]).every(f => this.accept
        .split(',')
        .map(condition => condition.trim())
        .map((condition) => {
          // file extension like .png
          if (/\.\w+$/.test(condition)) {
            return new RegExp(`${condition}$`, 'i').test(f.name)
          }

          return new RegExp(`^${condition}`, 'i').test(f.type)
        })
        .some(passed => passed))

      if (!passesAccept) {
        this.$refs.fileInput.value = ''
        this.$emit('input', null)
        return
      }

      this.$emit('input', this.file)
    }
  }
}
</script>

<style lang="scss" scoped>
::-webkit-file-upload-button {
  @apply cursor-pointer #{!important};
}
</style>
